import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  UserMinus, ChevronLeft, Plus, Search, X, Trash2,
  Loader2, Save, Settings, ArrowLeft, Phone, Mail, Check, ClipboardCheck,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useQuery } from "../../lib/useQuery";
import { supabase } from "../../lib/supabase";
import { useToast } from "../Toast";
import LoadingSkeleton from "../LoadingSkeleton";
import ConfirmDeleteDrawer from "../ConfirmDeleteDrawer";

const DEFAULT_MOTIVOS = [
  { name: "Cambio Residencia", sort_order: 1 },
  { name: "Descontento", sort_order: 2 },
  { name: "Económico", sort_order: 3 },
  { name: "Salud", sort_order: 4 },
  { name: "Falta de tiempo", sort_order: 5 },
  { name: "Otro", sort_order: 6 },
];

const DEFAULT_ACTION_TYPES = [
  { name: "Dar de baja en nuestro sistema", sort_order: 1 },
  { name: "Mensaje de despedida", sort_order: 2 },
];

function calcularLTV(fechaBaja, fechaAlta, cuotaMensual) {
  if (!fechaBaja || !fechaAlta || !cuotaMensual) return 0;
  const baja = new Date(fechaBaja);
  const alta = new Date(fechaAlta);
  const meses =
    (baja.getFullYear() - alta.getFullYear()) * 12 +
    (baja.getMonth() - alta.getMonth());
  return Math.max(0, meses * cuotaMensual);
}

function OptionsEditor({ options, bajas, onSave, onBack, saving, title = "Motivo de Baja", field = "motivo", placeholder = "Nombre de la opción" }) {
  const [items, setItems] = useState(options.map(o => ({ ...o })));
  const [visible, setVisible] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const addItem = () => {
    const maxOrder = items.length > 0 ? Math.max(...items.map(i => i.sort_order)) : 0;
    setItems([...items, { id: `new-${Date.now()}`, name: "", sort_order: maxOrder + 1, isNew: true }]);
  };

  const updateItem = (idx, value) => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], name: value };
    setItems(newItems);
  };

  const removeItem = (idx) => {
    const item = items[idx];
    if (!item.isNew && field && bajas) {
      const usedBy = bajas.filter(b => b[field] === item.name);
      if (usedBy.length > 0) {
        setDeleteError(`No se puede eliminar "${item.name}" porque está siendo usado por ${usedBy.length} baja(s).`);
        return;
      }
    }
    setDeleteError(null);
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    const validItems = items.filter(i => i.name.trim());
    onSave(validItems);
  };

  const handleBack = () => {
    setVisible(false);
    setTimeout(onBack, 200);
  };

  const content = (
    <>
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
        <button onClick={handleBack} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft size={18} className="text-gray-500" />
        </button>
        <span className="text-base font-semibold text-gray-900">Opciones: {title}</span>
      </div>

      <div className="flex-1 px-6 py-4 overflow-y-auto">
        {deleteError && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
            {deleteError}
          </div>
        )}

        {items.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No hay opciones configuradas</p>
        ) : (
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div key={item.id || idx} className="flex items-center gap-2">
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => updateItem(idx, e.target.value)}
                  placeholder={placeholder}
                  className="flex-1 px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-brand"
                />
                <button
                  onClick={() => removeItem(idx)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={addItem}
          className="mt-4 w-full py-2.5 text-sm text-brand border border-dashed border-brand/40 rounded-lg hover:bg-brand-light flex items-center justify-center gap-1.5 transition-colors"
        >
          <Plus size={16} /> Añadir opción
        </button>
      </div>

      <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
        <button onClick={handleBack} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Guardar
        </button>
      </div>
    </>
  );

  const drawer = (
    <div className="fixed inset-0 z-[999999]">
      <div
        className={`hidden md:flex fixed top-0 right-0 h-full w-full max-w-md flex-col bg-white shadow-2xl border-l border-gray-200 transition-transform duration-200 ease-out ${visible ? "translate-x-0" : "translate-x-full"}`}
      >
        {content}
      </div>
      <div
        className={`md:hidden fixed inset-x-0 bottom-0 top-0 bg-white flex flex-col transition-transform duration-200 ease-out ${visible ? "translate-y-0" : "translate-y-full"}`}
      >
        {content}
      </div>
    </div>
  );

  return createPortal(drawer, document.body);
}

function ActionsConfigDrawer({ types, completions, onSave, onClose, saving }) {
  const [items, setItems] = useState(types.map(t => ({ ...t })));
  const [visible, setVisible] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => setVisible(true));
    return () => { document.body.style.overflow = ""; };
  }, []);

  const close = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const addItem = () => {
    const maxOrder = items.length > 0 ? Math.max(...items.map(i => i.sort_order)) : 0;
    setItems([...items, { id: `new-${Date.now()}`, name: "", sort_order: maxOrder + 1, isNew: true }]);
  };

  const updateItem = (idx, value) => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], name: value };
    setItems(newItems);
  };

  const removeItem = (idx) => {
    const item = items[idx];
    if (!item.isNew) {
      const usedBy = (completions || []).filter(c => c.action_type_id === item.id && c.completed);
      if (usedBy.length > 0) {
        setDeleteError(`No se puede eliminar "${item.name}" porque tiene ${usedBy.length} check(s) marcado(s). Desmárcalos primero.`);
        return;
      }
    }
    setDeleteError(null);
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    const validItems = items.filter(i => i.name.trim());
    onSave(validItems);
  };

  const content = (
    <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Settings size={18} className="text-brand" />
          Configurar Acciones
        </h3>
        <button onClick={close} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <X size={18} className="text-gray-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <p className="text-sm text-gray-500 mb-4">
          Define las acciones de seguimiento que aparecerán para todas las bajas.
        </p>

        {deleteError && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
            {deleteError}
          </div>
        )}

        {items.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No hay acciones configuradas</p>
        ) : (
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div key={item.id || idx} className="flex items-center gap-2">
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => updateItem(idx, "name" in item ? e.target.value : e.target.value)}
                  placeholder="Nombre de la acción"
                  className="flex-1 px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-brand"
                />
                <button
                  onClick={() => removeItem(idx)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={addItem}
          className="mt-4 w-full py-2.5 text-sm text-brand border border-dashed border-brand/40 rounded-lg hover:bg-brand-light flex items-center justify-center gap-1.5 transition-colors"
        >
          <Plus size={16} /> Añadir acción
        </button>
      </div>

      <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
        <button onClick={close} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Guardar
        </button>
      </div>
    </>
  );

  const drawer = (
    <div className="fixed inset-0 z-[99999]">
      <div className={`fixed inset-0 bg-black/40 transition-opacity duration-200 ${visible ? "opacity-100" : "opacity-0"}`} onClick={close} />
      <div className={`hidden md:flex fixed top-0 right-0 h-full w-full max-w-md flex-col bg-white shadow-2xl transition-transform duration-200 ease-out ${visible ? "translate-x-0" : "translate-x-full"}`}>
        {content}
      </div>
      <div className={`md:hidden fixed inset-0 bg-white flex flex-col transition-transform duration-200 ease-out ${visible ? "translate-y-0" : "translate-y-full"}`}>
        {content}
      </div>
    </div>
  );

  return createPortal(drawer, document.body);
}

function BajaForm({
  baja, bajas, onSave, onClose, saving,
  motivoOptions, onSaveMotivos, savingMotivos,
  servicioOptions, onSaveServicios, savingServicios,
  actionTypes, completions, onToggleAction, updatingAction,
}) {
  const [form, setForm] = useState({
    nombre: "",
    telefono: "",
    email: "",
    tipo_servicio: "",
    cuota_mensual: "",
    fecha_baja: new Date().toISOString().split("T")[0],
    fecha_alta: "",
    motivo: "",
    comentarios: "",
  });
  const [visible, setVisible] = useState(false);
  const [editingMotivos, setEditingMotivos] = useState(false);
  const [editingServicios, setEditingServicios] = useState(false);
  const isEditing = !!baja?.id;

  useEffect(() => {
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => setVisible(true));
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    if (baja) {
      setForm({
        nombre: baja.nombre || "",
        telefono: baja.telefono || "",
        email: baja.email || "",
        tipo_servicio: baja.tipo_servicio || "",
        cuota_mensual: baja.cuota_mensual || "",
        fecha_baja: baja.fecha_baja || new Date().toISOString().split("T")[0],
        fecha_alta: baja.fecha_alta || "",
        motivo: baja.motivo || "",
        comentarios: baja.comentarios || "",
      });
    }
  }, [baja]);

  const close = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const ltv = calcularLTV(form.fecha_baja, form.fecha_alta, parseFloat(form.cuota_mensual) || 0);

  const handleSubmit = () => {
    if (!form.nombre.trim() || !form.fecha_baja) return;
    onSave(form);
  };

  const handleSaveMotivos = (items) => {
    onSaveMotivos(items).then(() => setEditingMotivos(false));
  };

  const handleSaveServicios = (items) => {
    onSaveServicios(items).then(() => setEditingServicios(false));
  };

  const isActionCompleted = (typeId) => {
    return completions?.some(c => c.action_type_id === typeId && c.completed) || false;
  };

  const inputClass = "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none";
  const selectClass = `${inputClass} bg-white`;

  const formContent = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Nombre <span className="text-red-500">*</span></label>
          <input type="text" value={form.nombre} onChange={e => update("nombre", e.target.value)} className={inputClass} placeholder="Nombre del cliente" autoFocus />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Teléfono</label>
          <input type="tel" value={form.telefono} onChange={e => update("telefono", e.target.value)} className={inputClass} placeholder="612 345 678" />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
          <input type="email" value={form.email} onChange={e => update("email", e.target.value)} className={inputClass} placeholder="cliente@ejemplo.com" />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tipo Servicio</label>
          <div className="flex gap-1.5">
            <select value={form.tipo_servicio} onChange={e => update("tipo_servicio", e.target.value)} className={`${selectClass} flex-1`}>
              <option value="">Seleccionar...</option>
              {servicioOptions.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
            </select>
            <button type="button" onClick={() => setEditingServicios(true)} className="px-2.5 py-2 text-gray-400 hover:text-brand hover:bg-brand-light border border-gray-200 rounded-lg transition-colors">
              <Settings size={16} />
            </button>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Cuota Mensual (€)</label>
          <input type="number" value={form.cuota_mensual} onChange={e => update("cuota_mensual", e.target.value)} className={inputClass} placeholder="0.00" step="0.01" />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Fecha Alta</label>
          <input type="date" value={form.fecha_alta} onChange={e => update("fecha_alta", e.target.value)} className={inputClass} />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Fecha Baja <span className="text-red-500">*</span></label>
          <input type="date" value={form.fecha_baja} onChange={e => update("fecha_baja", e.target.value)} className={inputClass} />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">LTV (calculado)</label>
          <div className="px-3 py-2 text-sm bg-gray-50 rounded-lg font-medium text-gray-700">
            {ltv.toFixed(2)} €
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Motivo</label>
          <div className="flex gap-1.5">
            <select value={form.motivo} onChange={e => update("motivo", e.target.value)} className={`${selectClass} flex-1`}>
              <option value="">Seleccionar...</option>
              {motivoOptions.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
            </select>
            <button type="button" onClick={() => setEditingMotivos(true)} className="px-2.5 py-2 text-gray-400 hover:text-brand hover:bg-brand-light border border-gray-200 rounded-lg transition-colors">
              <Settings size={16} />
            </button>
          </div>
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Comentarios</label>
          <textarea value={form.comentarios} onChange={e => update("comentarios", e.target.value)} rows={2} className={inputClass} placeholder="Notas..." />
        </div>
      </div>

      {isEditing && actionTypes.length > 0 && (
        <div className="border-t pt-4">
          <p className="text-xs font-medium text-gray-500 mb-3">Acciones de seguimiento</p>
          <div className="space-y-2">
            {actionTypes.map(type => {
              const completed = isActionCompleted(type.id);
              const isUpdating = updatingAction === `${baja.id}-${type.id}`;
              return (
                <button
                  key={type.id}
                  onClick={() => onToggleAction(baja.id, type.id, completed)}
                  disabled={isUpdating}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                    completed
                      ? "bg-emerald-50 border-emerald-200"
                      : "bg-amber-50 border-amber-200"
                  } ${isUpdating ? "opacity-50" : "hover:opacity-80"}`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-all ${
                    completed ? "bg-emerald-500 border-emerald-500 text-white" : "border-gray-300 bg-white"
                  }`}>
                    {isUpdating ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : completed ? (
                      <Check size={12} />
                    ) : null}
                  </div>
                  <span className={`text-sm font-medium ${completed ? "text-emerald-700" : "text-amber-700"}`}>
                    {type.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const mainPanel = (
    <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <span className="text-base font-semibold text-gray-900">{isEditing ? "Editar Baja" : "Nueva Baja"}</span>
        <button onClick={close} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <X size={18} className="text-gray-400" />
        </button>
      </div>
      <div className="flex-1 px-6 py-6 overflow-y-auto">{formContent}</div>
      <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
        <button onClick={close} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">Cancelar</button>
        <button
          onClick={handleSubmit}
          disabled={saving || !form.nombre.trim() || !form.fecha_baja}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark disabled:bg-gray-200 disabled:text-gray-400 rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {isEditing ? "Guardar" : "Crear"}
        </button>
      </div>
    </>
  );

  const panel = (
    <div className="fixed inset-0 z-[99999]">
      <div className={`fixed inset-0 bg-black/40 transition-opacity duration-250 ${visible ? "opacity-100" : "opacity-0"}`} onClick={close} />
      <div className={`hidden md:flex fixed top-0 right-0 h-full w-full max-w-xl flex-col bg-white shadow-2xl transition-transform duration-250 ease-out ${visible ? "translate-x-0" : "translate-x-full"}`}>
        {mainPanel}
      </div>
      <div className={`md:hidden fixed inset-0 bg-white transition-transform duration-250 ease-out ${visible ? "translate-y-0" : "translate-y-full"} flex flex-col`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <span className="text-base font-semibold text-gray-900">{isEditing ? "Editar Baja" : "Nueva Baja"}</span>
          <button onClick={close} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-400" />
          </button>
        </div>
        <div className="flex-1 px-5 py-4 overflow-y-auto">{formContent}</div>
        <div className="px-5 py-4 border-t border-gray-100 flex gap-3">
          <button onClick={close} className="flex-1 px-4 py-3 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">Cancelar</button>
          <button
            onClick={handleSubmit}
            disabled={saving || !form.nombre.trim() || !form.fecha_baja}
            className="flex-1 px-4 py-3 text-sm font-medium text-white bg-brand hover:bg-brand-dark disabled:bg-gray-200 disabled:text-gray-400 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {isEditing ? "Guardar" : "Crear"}
          </button>
        </div>
      </div>

      {editingMotivos && (
        <OptionsEditor
          options={motivoOptions}
          bajas={bajas}
          onSave={handleSaveMotivos}
          onBack={() => setEditingMotivos(false)}
          saving={savingMotivos}
          title="Motivo de Baja"
          field="motivo"
          placeholder="Nombre del motivo"
        />
      )}

      {editingServicios && (
        <OptionsEditor
          options={servicioOptions}
          bajas={bajas}
          onSave={handleSaveServicios}
          onBack={() => setEditingServicios(false)}
          saving={savingServicios}
          title="Tipo de Servicio"
          field="tipo_servicio"
          placeholder="Nombre del servicio"
        />
      )}
    </div>
  );

  return createPortal(panel, document.body);
}

export default function ControlBajas({ onBack }) {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingBaja, setEditingBaja] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingMotivos, setSavingMotivos] = useState(false);
  const [savingServicios, setSavingServicios] = useState(false);
  const [savingActions, setSavingActions] = useState(false);
  const [showActionsConfig, setShowActionsConfig] = useState(false);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [updatingAction, setUpdatingAction] = useState(null);

  const { data: bajas, loading, refetch } = useQuery(async () => {
    const { data, error } = await supabase.from("bajas").select("*").eq("student_id", user.id).order("fecha_baja", { ascending: false });
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const { data: motivoOptions, loading: loadingMotivos, refetch: refetchMotivos } = useQuery(async () => {
    const { data, error } = await supabase.from("motivo_baja_options").select("*").eq("student_id", user.id).order("sort_order");
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const { data: servicioOptions, loading: loadingServicios, refetch: refetchServicios } = useQuery(async () => {
    const { data, error } = await supabase.from("servicio_options").select("*").eq("student_id", user.id).order("sort_order");
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const { data: actionTypes, loading: loadingActionTypes, refetch: refetchActionTypes } = useQuery(async () => {
    const { data, error } = await supabase.from("baja_action_types").select("*").eq("student_id", user.id).order("sort_order");
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const { data: actionCompletions, loading: loadingCompletions, refetch: refetchCompletions } = useQuery(async () => {
    const { data, error } = await supabase.from("baja_action_completions").select("*").eq("student_id", user.id);
    if (error) throw error;
    return data || [];
  }, [user.id]);

  useEffect(() => {
    if (!loadingMotivos && motivoOptions && motivoOptions.length === 0) {
      const inserts = DEFAULT_MOTIVOS.map(m => ({ student_id: user.id, name: m.name, sort_order: m.sort_order }));
      supabase.from("motivo_baja_options").insert(inserts).then(() => refetchMotivos());
    }
  }, [loadingMotivos, motivoOptions]);

  useEffect(() => {
    if (!loadingActionTypes && actionTypes && actionTypes.length === 0) {
      const inserts = DEFAULT_ACTION_TYPES.map(a => ({ student_id: user.id, name: a.name, sort_order: a.sort_order }));
      supabase.from("baja_action_types").insert(inserts).then(() => refetchActionTypes());
    }
  }, [loadingActionTypes, actionTypes]);

  const handleSaveMotivos = async (items) => {
    setSavingMotivos(true);
    try {
      const existingIds = (motivoOptions || []).map(o => o.id);
      const newItemIds = items.filter(i => !i.isNew).map(i => i.id);
      const toDelete = existingIds.filter(id => !newItemIds.includes(id));
      if (toDelete.length > 0) await supabase.from("motivo_baja_options").delete().in("id", toDelete);
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.isNew) {
          await supabase.from("motivo_baja_options").insert({ student_id: user.id, name: item.name, sort_order: i + 1 });
        } else {
          await supabase.from("motivo_baja_options").update({ name: item.name, sort_order: i + 1 }).eq("id", item.id);
        }
      }
      await refetchMotivos();
      showSuccess("Motivos guardados");
    } catch {
      showError("No se pudieron guardar los motivos");
    } finally {
      setSavingMotivos(false);
    }
  };

  const handleSaveServicios = async (items) => {
    setSavingServicios(true);
    try {
      const existingIds = (servicioOptions || []).map(o => o.id);
      const newItemIds = items.filter(i => !i.isNew).map(i => i.id);
      const toDelete = existingIds.filter(id => !newItemIds.includes(id));
      if (toDelete.length > 0) await supabase.from("servicio_options").delete().in("id", toDelete);
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.isNew) {
          await supabase.from("servicio_options").insert({ student_id: user.id, name: item.name, sort_order: i + 1 });
        } else {
          await supabase.from("servicio_options").update({ name: item.name, sort_order: i + 1 }).eq("id", item.id);
        }
      }
      await refetchServicios();
      showSuccess("Tipos de servicio guardados");
    } catch {
      showError("No se pudieron guardar los tipos de servicio");
    } finally {
      setSavingServicios(false);
    }
  };

  const handleSaveActionTypes = async (items) => {
    setSavingActions(true);
    try {
      const existingIds = (actionTypes || []).map(t => t.id);
      const newItemIds = items.filter(i => !i.isNew).map(i => i.id);
      const toDelete = existingIds.filter(id => !newItemIds.includes(id));
      if (toDelete.length > 0) await supabase.from("baja_action_types").delete().in("id", toDelete);
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.isNew) {
          await supabase.from("baja_action_types").insert({ student_id: user.id, name: item.name, sort_order: i + 1 });
        } else {
          await supabase.from("baja_action_types").update({ name: item.name, sort_order: i + 1 }).eq("id", item.id);
        }
      }
      await refetchActionTypes();
      showSuccess("Acciones guardadas");
      setShowActionsConfig(false);
    } catch {
      showError("No se pudieron guardar las acciones");
    } finally {
      setSavingActions(false);
    }
  };

  const toggleAction = async (bajaId, typeId, currentCompleted) => {
    const key = `${bajaId}-${typeId}`;
    setUpdatingAction(key);
    try {
      const existing = (actionCompletions || []).find(c => c.baja_id === bajaId && c.action_type_id === typeId);
      if (existing) {
        await supabase.from("baja_action_completions").update({
          completed: !currentCompleted,
          completed_at: !currentCompleted ? new Date().toISOString() : null,
        }).eq("id", existing.id);
      } else {
        await supabase.from("baja_action_completions").insert({
          student_id: user.id,
          baja_id: bajaId,
          action_type_id: typeId,
          completed: true,
          completed_at: new Date().toISOString(),
        });
      }
      await refetchCompletions();
    } catch {
      showError("Error al actualizar acción");
    } finally {
      setUpdatingAction(null);
    }
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      const payload = {
        student_id: user.id,
        nombre: formData.nombre,
        telefono: formData.telefono || null,
        email: formData.email || null,
        tipo_servicio: formData.tipo_servicio || null,
        cuota_mensual: parseFloat(formData.cuota_mensual) || 0,
        fecha_baja: formData.fecha_baja,
        fecha_alta: formData.fecha_alta || null,
        motivo: formData.motivo || null,
        comentarios: formData.comentarios || null,
        updated_at: new Date().toISOString(),
      };

      if (editingBaja?.id) {
        const { error } = await supabase.from("bajas").update(payload).eq("id", editingBaja.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("bajas").insert(payload);
        if (error) throw error;
      }

      await refetch();
      showSuccess(editingBaja?.id ? "Baja actualizada" : "Baja registrada");
      setShowForm(false);
      setEditingBaja(null);
    } catch {
      showError("No se pudo guardar. Inténtalo de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const { error } = await supabase.from("bajas").delete().eq("id", id);
    if (error) {
      showError("No se pudo eliminar");
      return;
    }
    refetch();
    setDeleteTarget(null);
  };

  const getActionProgress = (bajaId) => {
    const types = actionTypes || [];
    if (types.length === 0) return null;
    const completed = types.filter(t =>
      (actionCompletions || []).some(c => c.baja_id === bajaId && c.action_type_id === t.id && c.completed)
    ).length;
    return { completed, total: types.length };
  };

  const bajasWithLTV = (bajas || []).map(b => ({
    ...b,
    ltvCalculado: calcularLTV(b.fecha_baja, b.fecha_alta, b.cuota_mensual),
  }));

  const filteredBajas = bajasWithLTV.filter(b => {
    if (search) {
      const s = search.toLowerCase();
      if (!b.nombre?.toLowerCase().includes(s) && !b.motivo?.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const isLoading = loading || loadingMotivos || loadingServicios || loadingActionTypes || loadingCompletions;
  if (isLoading) return <LoadingSkeleton />;

  const bajaCompletions = editingBaja
    ? (actionCompletions || []).filter(c => c.baja_id === editingBaja.id)
    : [];

  return (
    <div className="animate-fade-in">
      <button onClick={onBack} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm mb-6">
        <ChevronLeft size={18} /> Volver a Herramientas
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center">
            <UserMinus size={24} className="text-red-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Control de Bajas</h2>
            <p className="text-sm text-gray-500">{bajas?.length || 0} baja(s) registrada(s)</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowActionsConfig(true)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Settings size={16} />
            Configurar Acciones
          </button>
          <button
            onClick={() => { setEditingBaja(null); setShowForm(true); }}
            className="px-4 py-2.5 bg-brand text-white font-medium rounded-lg hover:bg-brand-dark transition-colors flex items-center justify-center gap-2"
          >
            <Plus size={18} /> Nueva Baja
          </button>
        </div>
      </div>

      <div className="mb-4">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre o motivo..."
            className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
          />
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-3">{filteredBajas.length} baja(s) encontrada(s)</p>

      {filteredBajas.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
          {filteredBajas.map(baja => {
            const progress = getActionProgress(baja.id);
            return (
              <div
                key={baja.id}
                onClick={() => { setEditingBaja(baja); setShowForm(true); }}
                className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900 truncate">{baja.nombre}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span>Baja: {new Date(baja.fecha_baja).toLocaleDateString("es-ES")}</span>
                      {baja.tipo_servicio && <span>{baja.tipo_servicio}</span>}
                      {baja.cuota_mensual > 0 && <span>{baja.cuota_mensual}€/mes</span>}
                      {baja.motivo && <span className="text-amber-600">{baja.motivo}</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs">
                      <span className="text-gray-400">LTV: {baja.ltvCalculado.toFixed(2)}€</span>
                      {progress && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${
                          progress.completed === progress.total
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}>
                          <ClipboardCheck size={12} />
                          {progress.completed}/{progress.total}
                        </span>
                      )}
                      {(baja.telefono || baja.email) && (
                        <span className="flex items-center gap-2 text-gray-400">
                          {baja.telefono && <span className="flex items-center gap-1"><Phone size={11} />{baja.telefono}</span>}
                          {baja.email && <span className="flex items-center gap-1"><Mail size={11} />{baja.email}</span>}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(baja); }}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <UserMinus size={48} className="mx-auto text-gray-300 mb-4" />
          <p>No se encontraron bajas</p>
          <p className="text-sm mt-1">Ajusta la búsqueda o registra una nueva baja</p>
        </div>
      )}

      {showForm && (
        <BajaForm
          baja={editingBaja}
          bajas={bajas || []}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditingBaja(null); }}
          saving={saving}
          motivoOptions={motivoOptions || []}
          onSaveMotivos={handleSaveMotivos}
          savingMotivos={savingMotivos}
          servicioOptions={servicioOptions || []}
          onSaveServicios={handleSaveServicios}
          savingServicios={savingServicios}
          actionTypes={actionTypes || []}
          completions={bajaCompletions}
          onToggleAction={toggleAction}
          updatingAction={updatingAction}
        />
      )}

      {showActionsConfig && (
        <ActionsConfigDrawer
          types={actionTypes || []}
          completions={actionCompletions || []}
          onSave={handleSaveActionTypes}
          onClose={() => setShowActionsConfig(false)}
          saving={savingActions}
        />
      )}

      {deleteTarget && (
        <ConfirmDeleteDrawer
          title="Eliminar baja"
          itemName={deleteTarget.nombre}
          message="¿Estás seguro de que quieres eliminar este registro de baja?"
          onConfirm={() => handleDelete(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
